import {Injectable} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {IgenomicsState} from '../../core/interfaces/genomics-state.interface';
import {
  CurrentActiveWorkflowState,
  GetResourcesMapState,
  InitCurrentActiveWorkflowState,
  InitGetResourcesMapState,
  setActiveWorkflow,
  setResourceMap,
  startFetchingWorkflowUiConfig
} from '../store/workflow-config.actions';
import {
  selectActiveWorkflow,
  selectRequestedRevision,
  selectRequestedRevisionId,
  selectResourceMap
} from '../store/workflow-config.selectors';
import {WorkflowUiConfig} from '../interfaces/workflow-ui-config.interface';
import {Observable} from 'rxjs/Observable';
import {IWorkflowRequest} from '../interfaces/workflow-request.interface';
import {clearAllCachedItems} from '../../core/store/core.actions';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  flatMap,
  map,
  pluck,
  skip,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
  zip
} from 'rxjs/operators';
import {WorkflowService} from './workflow.service';
import {of} from 'rxjs/observable/of';
import {IResourcesMap} from '../interfaces/resource-map.interface';
import {InterpolateService} from '../../shared/services/interpolate.service';
import {Resources} from '../constants/resources.enum';
import {Workflow, WorkflowFeatures} from '../../core/interfaces/workflow.interface';
import {IDiagnosticOrder} from '@ispm/test/interfaces/diagnostic-order.interface';
import {CoreActionCreatorService} from '@ispm/core/services/core-action-creator.service';
import {
  selectCurrentConfig,
  selectTargetStep,
  selectUiConfiguration
} from '../../configuration/store/configuration.selector';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { get,isEmpty } from 'lodash';
import { NOTIFICATION_MESSAGE } from "@ispm-client/modules/aggregator/services/consts";
import { Actions, ofType } from '@ngrx/effects';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { from } from "rxjs";
import { combineLatest } from 'rxjs/observable/combineLatest';
import { IActionToDispatch } from "@ispm/shared/interfaces/action.interface";
import {RouterManagerService} from "@ispm/core/services/router-manager.service";

@Injectable()
export class WorkflowConfigService {

  static readonly PROCESS_CONFIG_INPUT_TYPE: string = 'workflowProcessConfiguration';

  public activeWorkflow$: Observable<IWorkflowRequest> = this.store.select(selectActiveWorkflow);
  public resourcesMap$: Observable<IResourcesMap> = this.store.select(selectResourceMap);
  public cacheKey: string;

  private cacheHandlers: Array<(cacheKey: string) => void> = [];

  constructor(private store: Store<IgenomicsState>,
    private interpolate: InterpolateService,
    private workflowService: WorkflowService,
    private _coreAC: CoreActionCreatorService,
    private notificationMessage: NOTIFICATION_MESSAGE,
    private notificationMessageService: NotificationMessageService,
    private actions$ : Actions,
    private routerManagerService: RouterManagerService
  ) {
    this.subscribeCurrentActiveWorkflow(actions$);
    this.subscribeGetResourcesMap(actions$);
  }

  public static generateIdFromWorkflow(workflowRequest: IWorkflowRequest, useIteration: boolean = true): string {
    let activeJobId;
    let iteration = 1;
    const activeRevision = workflowRequest.revisions.find(rev => rev.id === workflowRequest['viewRevisionId']);

    if (workflowRequest['viewRevisionId'] === workflowRequest.activeRevision) {
      activeJobId = workflowRequest.activeJob;

      if (useIteration) {
        const activeJob = activeRevision.items.find(item => item.id === activeJobId);
        iteration = activeJob ? parseInt(activeJob.iteration) : iteration;
      }
    } else {
      activeJobId = activeRevision.status;
    }

    return `${workflowRequest.id}--${workflowRequest['viewRevisionId']}--${activeJobId}--${iteration}`;
  }

  public changeWorkflow(workflowId: string, revisionId: string,additionalPayload = {}): Observable<IWorkflowRequest | { }> {
    if(!isEmpty(additionalPayload)) {
      return this.clearAllCachedData()
        .pipe(
          tap(() => this.store.dispatch(startFetchingWorkflowUiConfig({workflowId, revisionId}))),
          flatMap(() => {
            return !workflowId
              ? Observable.throw('HANDLED')
              : Observable.of(true);
          }),
          switchMap(() => this.fetchRevisionResourcesMap(revisionId, additionalPayload).pipe(switchMap(() => this.fetchActiveWorkflow(workflowId, revisionId)))),
          map((res) => res[1]),
          catchError(err => err === 'HANDLED' ? Observable.empty() : Observable.throw(err))
        );
    }
    return this.clearAllCachedData()
      .pipe(
        tap(() => this.store.dispatch(startFetchingWorkflowUiConfig({ workflowId, revisionId}))),
        flatMap(() => {
          return !workflowId
            ? Observable.throw('HANDLED')
            : Observable.of(true);
        }),
        zip(...this.populateStore(workflowId, revisionId)),
        map((res) => res[1]),
        catchError(err => err === 'HANDLED' ? Observable.empty() : Observable.throw(err))
      );
  }

  public changeWorkflowWithoutClearingCach(workflowId: string, revisionId: string) {
    return this.initActiveWorkflow()
      .pipe(
        tap(() => this.store.dispatch(startFetchingWorkflowUiConfig({ workflowId, revisionId}))),
        flatMap(() => {
          return !workflowId
            ? Observable.throw('HANDLED')
            : Observable.of(true);
        }),
        zip(...this.populateStore(workflowId, revisionId)),
        map((res) => res[1]),
        catchError(err => err === 'HANDLED' ? Observable.empty() : Observable.throw(err))
      );
  }

  protected populateStore(workflowId: string, revisionId: string) {
    return [
      this.fetchActiveWorkflow(workflowId, revisionId),
      this.fetchRevisionResourcesMap(revisionId)
    ];
  }

  public getUiConfig(): Observable<WorkflowUiConfig> {
    return this.store.pipe(
      select(selectActiveWorkflow),
      filter(data => !!data),
      switchMap(() => {
        return forkJoin(
          this.store.pipe(select(selectUiConfiguration('activeTabs', 'Test')), take(1)),
          this.store.pipe(select(selectUiConfiguration('workflowVisibility', 'Test')), take(1))
        );
      }),
      map(([activeTabs, workflowVisibility]) => ({ activeTabs, workflowVisibility}))
    );
  }

  public async setActiveWorkflow(workflowRequest: IWorkflowRequest) {
    if (workflowRequest) {
      workflowRequest['viewRevisionId'] =  workflowRequest['viewRevisionId'] || this.routerManagerService.parameters.revisionId;
      const configuration = await this.store.pipe(select(selectCurrentConfig('Test')), take(1)).toPromise();
      const useIteration = get(configuration, 'cache.useIteration', true);
      workflowRequest['cacheKey'] = WorkflowConfigService.generateIdFromWorkflow(workflowRequest, useIteration);
      this.cacheKey = workflowRequest['cacheKey'];
    } else {
      this.cacheKey = null;
    }

    this.store.dispatch(setActiveWorkflow(workflowRequest));
  }

  public getResourcesMap(): Observable<IResourcesMap> {
    return this.resourcesMap$.filter(resourcesMap => !!resourcesMap).take(1);
  }

  public setResourcesMap(resourceMap: IResourcesMap) {
    this.store.dispatch(setResourceMap(resourceMap));
  }

  public interpolateWithResourcesMap(interpolateString: string, fallbackObject: { [prop: string]: string } = { }, forcePropertiesObject: { [prop: string]: string } = { }): Observable<string> {
    let workflow;

    return this.getCurrentActiveWorkflow().pipe(
      flatMap((_workflow) => {
        workflow = _workflow;
        return this.getResourcesMap();
      }),
      flatMap((resourceMap) => {
        return Workflow.isFeatureAvailable(workflow, resourceMap[Resources.RequestedRevision], WorkflowFeatures.REVISE_CHECKPOINTS)
          ? of(resourceMap)
          : this.propertiesPopulate(
              fallbackObject).pipe(map((data) => {
                fallbackObject = data;

                return resourceMap;
              })
            );
      }),
      flatMap((resourceMap) => this.propertiesPopulate(forcePropertiesObject).pipe(map(() => resourceMap))),
      map(resourceMap => {
        return this.interpolate.interpolate(interpolateString, Object.assign({ }, fallbackObject, resourceMap, forcePropertiesObject));
      })
    );
  }

  public getRequestedRevisionId() {
    return this.store.pipe(
      select(selectRequestedRevisionId),
      filter(requestedRevisionId => !!requestedRevisionId),
    );
  }

  public getRequestedRevision() {
    return this.store.pipe(
      select(selectRequestedRevision),
      filter(requestedRevision => !!requestedRevision),
    );
  }

  public clearAllCachedData() {
    return from(this.setActiveWorkflow(null)).pipe(
      switchMap(() => {
        this.workflowService.onGoingCalls = { };

        return this.getCacheKey(true)
          .pipe(
            map(key => {
              this.store.dispatch(clearAllCachedItems(key));

              return key;
            }),
            map(key => this.emitAllHandlers(key))
          );
      })
    );
  }

  public initActiveWorkflow() {
    return from(this.setActiveWorkflow(null)).pipe(
      switchMap(() => {
        this.workflowService.onGoingCalls = { };
        return this.getCacheKey(true);
      })
    );
  }

  public getActiveWorkflow(listenUntilChanged: boolean = true, listenToKey: string = 'id'): Observable<IWorkflowRequest> {
    return listenUntilChanged
      ? this.activeWorkflow$.pipe(takeUntil(this.workflowChanged(listenToKey)))
      : this.activeWorkflow$;
  }

  public getCurrentActiveWorkflow(): Observable<IWorkflowRequest> {
    return this.activeWorkflow$
      .pipe(
        filter(workflow => !!workflow),
        take(1)
      );
  }

  public workflowChanged(listenToKey: string = 'id'): Observable<IWorkflowRequest> {
    return this.activeWorkflow$
      .pipe(
        distinctUntilChanged((prev, curr) => prev && curr && prev[listenToKey] === curr[listenToKey]),
        skip(1),
        take(1)
      );
  }

  public getCacheKey(returnFirstValue: boolean = false): Observable<string> {
    return this.activeWorkflow$.pipe(
      filter(workflowRequest => returnFirstValue || !!workflowRequest),
      take(1),
      flatMap(workflowRequest => Observable.of(workflowRequest || { })),
      pluck('cacheKey')
    );
  }

  public getCacheKeySync(): string {
    return this.cacheKey;
  }

  public addCacheHandler(...cacheHandlers: Array<(cacheKey: string) => void>) {
    this.cacheHandlers.push.apply(this.cacheHandlers, cacheHandlers);
  }

  /**
   * Replaces properties names with ids from resources map. Does not mutate the origin
   * @returns {Observable<any>}
   * @param propertiesObject
   * @param resourceMap
   * @param workflow
   */
  private getPopulatedProperties(propertiesObject: any, resourceMap, workflow): any {
    const populateRecursively = (objectNode) => {
      if (!objectNode) {
        return objectNode;
      }

      if (Array.isArray(objectNode)) {
        return Array.from(objectNode)
          .filter(item => !!item)
          .map(item => populateObjectNode(item));
      }

      if (typeof objectNode === 'object') {
        return Object.keys(objectNode)
          .reduce((accum, key) => {
            return { ...accum, [key]: key === 'resourceType' ? objectNode[key] : populateObjectNode(objectNode[key])};
          }, { });
      }

      return objectNode;
    };

    const populateObjectNode = (objectNode) => (
      typeof objectNode === 'string' ? this.populateEntry(objectNode, resourceMap, workflow) : populateRecursively(objectNode)
    );

    return populateRecursively(propertiesObject);
  }

  public propertiesPopulate(propertiesObject: any): Observable<any> {
    return combineLatest([
      this.getCurrentActiveWorkflow(),
      this.getResourcesMap(),
    ]).pipe(
      map(([workflow, resourceMap]) =>
        this.getPopulatedProperties(propertiesObject, resourceMap, workflow)
      ),
    );
  }

  private populateEntry(entry: string,  resourceMap, workflow): string {
    const properties = entry.split('|');

    for (const property of properties) {
      if (property in resourceMap) {
        entry = resourceMap[property];
        break;
      } else if (Workflow.isFeatureAvailable(workflow,
        resourceMap[Resources.RequestedRevision],
        WorkflowFeatures.REVISE_CHECKPOINTS)
      ) {
        break;
      }
    }

    return entry;
  }

  private fetchActiveWorkflow(workflowId: string, revisionId: string): Observable<IWorkflowRequest | { }> {
    return this.workflowService.getWorkflowRequest(workflowId, revisionId)
      .pipe(
        map(workflowRequest => {
          this.setActiveWorkflow(workflowRequest).then();

          return workflowRequest;
        }),
        catchError(() => of({ }))
      );
  }

  private fetchRevisionResourcesMap(revisionId: string,additionalPayload = {}): Observable<IResourcesMap | { }> {
    return this.workflowService.getRevisionResourceMap(revisionId,additionalPayload)
      .pipe(
        map((resourceMap: IResourcesMap) => {
          this.setResourcesMap(resourceMap);

          return resourceMap;
        }),
        catchError(() => of({ }))
      );
  }

  protected emitAllHandlers(cacheKey: string) {
    this.cacheHandlers.forEach(handler => handler(cacheKey));
  }

  public listenToWorkflowChange() {
    return this.getActiveWorkflow()
      .distinctUntilChanged()
      .skip(1)
      .take(1);
  }

  public performWorkflowStep(diagnosticOrder: IDiagnosticOrder, inputButtonArgs, payload: any, toggleLoader: boolean = true, partialData = null, actionToDispatchOnError: IActionToDispatch = null): any {
    if (toggleLoader) {
      this._coreAC.setLoader(true);
    }
    return this.getCurrentActiveWorkflow().pipe(
      withLatestFrom(
        this.store.pipe(select(selectTargetStep))
      ),
      flatMap(([workflow, targetStep]) => {
        return this.getResourcesMap().pipe(
            map((resourceMap) => {
              return { requestedRevision: resourceMap[Resources.RequestedRevision], activeJob: resourceMap[Resources.ActiveJob], workflow, targetStep};
            })
          );
      }),
      flatMap(({ workflow, requestedRevision, targetStep}) => {
        return this.workflowService.performWorkflowStep(diagnosticOrder, targetStep, null, requestedRevision, null, null, inputButtonArgs, payload, null, partialData);
      }),
      tap(_ => {
        this.notificationMessageService.success(this.notificationMessage.SUCCESSFULLY_MOVED_TO_NEXT_STEP);
      }),
      catchError(err => {
        console.error('Error with perform workflow step: %s', err);
        this.notificationMessageService.error(this.notificationMessage.UNABLE_TO_MOVE_TO_THE_NEXT_STEP);
        if(actionToDispatchOnError && actionToDispatchOnError.type) {
          this.store.dispatch(actionToDispatchOnError);
        }
        return Observable.throw(err);
      }),
      finalize(() => {
        if (toggleLoader) {
          this._coreAC.setLoader(false);
        }
      })
    );
  }

  public subscribeCurrentActiveWorkflow(actions$: Actions): void{
    actions$
      .pipe(ofType<InitCurrentActiveWorkflowState>(CurrentActiveWorkflowState.INIT))
      .subscribe(()=>{
        this.getCurrentActiveWorkflow();
      })
  }

  public subscribeGetResourcesMap(actions$: Actions): void{
    actions$
      .pipe(ofType<InitGetResourcesMapState>(GetResourcesMapState.INIT))
      .subscribe(()=>{
        this.getResourcesMap();
      })
  }
}
